import {
    ModalButtonStyles,
    OpenModal,
    UpdateData,
    useEnum,
    useTable,
} from '@composables/common'
import CookieConsentServiceEditForm from '@partials/cookieConsentServices/CookieConsentServiceEditForm.vue'
import { NameAndId } from '@src/components'
import {
    CookieConsentServiceCategory,
    CookieConsentServiceProviderKey,
} from '@src/enums'
import { CookieConsentService } from '@src/types'
import {
    createCookieConsentService,
    deleteCookieConsentService,
    reorderCookieConsentServices,
    seedDefaultCookieConsentServices,
    updateCookieConsentService,
} from '@src/utils/api/cookieConsentServices'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import { Ref, computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function usePlatformCookieConsentServicesTable(
    platformName: Ref<string | null>,
    cookieConsentServices: Ref<CookieConsentService[]>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const toast = useToastStore()
    const i18n = useI18n()
    const { getEnumLabel } = useEnum()

    const total = computed(() => {
        return cookieConsentServices.value.length
    })

    // Services in the "essential" category are always active and can not be
    // removed, so the delete action (and bulk-delete selection) is hidden for
    // those rows - see how `usePlatformEmailTemplatesTable` hides row actions
    // for `inherited` rows via a per-row `disabled`/`condition` function.
    const isEssential = (cookieConsentService: CookieConsentService) =>
        cookieConsentService.category === CookieConsentServiceCategory.ESSENTIAL

    const {
        TableWrapper: PlatformCookieConsentServicesTable,
        selected: selectedPlatformCookieConsentServicesIds,
    } = useTable({
        updateData: persistSortOrder as UpdateData,
        tableProps: {
            id: 'platform_cookie_consent_services',
            label: 'platform_cookie_consent_services',
            help: 'help_platform_cookie_consent_services_table',
            data: cookieConsentServices,
            total: total,
            sortableOrder: userStore.can('manage_cookie_consent_services'),
            itemOrderKey: 'position',
            itemIdKey: 'id',
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                    disabled: (row: CookieConsentService) => isEssential(row),
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'category',
                    key: 'category',
                    cellRenderer: (props: {
                        rowData: CookieConsentService
                    }) => {
                        return getEnumLabel(
                            CookieConsentServiceCategory,
                            props.rowData.category
                        )
                    },
                },
                {
                    label: 'provider_key',
                    key: 'provider_key',
                    cellRenderer: (props: {
                        rowData: CookieConsentService
                    }) => {
                        return getEnumLabel(
                            CookieConsentServiceProviderKey,
                            props.rowData.provider_key
                        )
                    },
                },
                {
                    label: 'name',
                    key: 'name',
                    cellRenderer: (props: {
                        rowData: CookieConsentService
                    }) => {
                        const name = props.rowData.name
                        return (
                            name?.[i18n.locale.value as 'de' | 'en' | 'it'] ??
                            name?.de ??
                            ''
                        )
                    },
                },
                {
                    label: 'enabled',
                    key: 'enabled',
                    cellRenderer: (props: {
                        rowData: CookieConsentService
                    }) => {
                        return props.rowData.enabled
                            ? i18n.t('yes')
                            : i18n.t('no')
                    },
                },
                {
                    label: 'rank',
                    key: 'position',
                    help: userStore.can('manage_cookie_consent_services')
                        ? 'help_drag_and_drop'
                        : '',
                    cellRenderer: (props: {
                        rowData: CookieConsentService
                    }) => {
                        return props.rowData.position + 1
                    },
                },
            ],
            actions: [
                {
                    action: handleEditOrCreateCookieConsentService,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('manage_cookie_consent_services'),
                },
                {
                    action: handleDeleteCookieConsentService,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('manage_cookie_consent_services')
                        ? (row: CookieConsentService) => !isEssential(row)
                        : false,
                },
            ],
        },
    })

    async function persistSortOrder() {
        if (!platformName.value) return
        await reorderCookieConsentServices(
            platformName.value,
            cookieConsentServices.value.map((cookieConsentService, index) => ({
                id: cookieConsentService.id as string,
                position: index,
            }))
        )
    }

    function handleDeleteCookieConsentService(
        cookieConsentService: CookieConsentService
    ) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_cookie_consent_service',
                title: 'modal_title_delete_platform_cookie_consent_service',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    deleteCookieConsentServiceItem(cookieConsentService),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [
                        {
                            ...cookieConsentService,
                            name: cookieConsentService.name?.[
                                i18n.locale.value as 'de' | 'en' | 'it'
                            ],
                        },
                    ],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function handleDeletePlatformCookieConsentServices() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_cookie_consent_services',
                title: 'modal_title_delete_platform_cookie_consent_services',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    deleteCookieConsentServices(
                        selectedCookieConsentServices.value
                    ),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedCookieConsentServices.value.map(
                        (cookieConsentService) => ({
                            ...cookieConsentService,
                            name: cookieConsentService.name?.[
                                i18n.locale.value as 'de' | 'en' | 'it'
                            ],
                        })
                    ),
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    async function deleteCookieConsentServiceItem(
        cookieConsentServiceToDelete: CookieConsentService
    ) {
        if (!platformName.value || !cookieConsentServiceToDelete.id) return

        try {
            await deleteCookieConsentService(
                platformName.value,
                cookieConsentServiceToDelete.id
            )
            cookieConsentServices.value.splice(
                cookieConsentServices.value.findIndex(
                    (cookieConsentService) =>
                        cookieConsentServiceToDelete.id ===
                        cookieConsentService.id
                ),
                1
            )
            toast.success(
                i18n.t('toast_success_cookie_consent_service_deleted', {
                    service:
                        cookieConsentServiceToDelete.name?.[
                            i18n.locale.value as 'de' | 'en' | 'it'
                        ],
                })
            )
        } finally {
            isModalOpen.value = false
        }
    }

    async function deleteCookieConsentServices(
        cookieConsentServicesToDelete: CookieConsentService[]
    ) {
        if (!platformName.value) return

        for (const cookieConsentService of cookieConsentServicesToDelete) {
            if (!cookieConsentService.id) continue
            await deleteCookieConsentService(
                platformName.value,
                cookieConsentService.id
            )
            cookieConsentServices.value.splice(
                cookieConsentServices.value.findIndex(
                    (item) => item.id === cookieConsentService.id
                ),
                1
            )
        }

        toast.success(
            i18n.t('toast_success_cookie_consent_services_deleted', {
                services: cookieConsentServicesToDelete
                    .map(
                        (cookieConsentService) =>
                            cookieConsentService.name?.[
                                i18n.locale.value as 'de' | 'en' | 'it'
                            ]
                    )
                    .join(', '),
            })
        )
        selectedPlatformCookieConsentServicesIds.value = []
        isModalOpen.value = false
    }

    function handleEditOrCreateCookieConsentService(
        cookieConsentService: CookieConsentService = new CookieConsentService()
    ) {
        const editedCookieConsentService = ref(new CookieConsentService())
        editedCookieConsentService.value = {
            ...cookieConsentService,
            name: { ...cookieConsentService.name },
            description: { ...cookieConsentService.description },
        }
        isModalOpen.value = true

        const disableCreate = computed(() => {
            return !(
                editedCookieConsentService.value.category &&
                editedCookieConsentService.value.provider_key &&
                editedCookieConsentService.value.name?.de &&
                editedCookieConsentService.value.name?.en &&
                editedCookieConsentService.value.name?.it
            )
        })

        openModal(
            {
                id: 'edit_platform_cookie_consent_service',
                title: editedCookieConsentService.value.id
                    ? 'modal_title_update_platform_cookie_consent_service'
                    : 'modal_title_create_platform_cookie_consent_service',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    saveCookieConsentService(editedCookieConsentService.value),
            },
            () =>
                h(CookieConsentServiceEditForm, {
                    modelValue: editedCookieConsentService,
                }),
            {
                submitButtonText: editedCookieConsentService.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    async function saveCookieConsentService(
        cookieConsentService: CookieConsentService
    ) {
        if (!platformName.value) return

        const isCreate = !cookieConsentService.id

        const response = isCreate
            ? await createCookieConsentService(
                  platformName.value,
                  cookieConsentService
              )
            : await updateCookieConsentService(
                  platformName.value,
                  cookieConsentService
              )

        const saved = response.data

        if (isCreate) {
            cookieConsentServices.value.push(saved)
        } else {
            const index = cookieConsentServices.value.findIndex(
                (item) => item.id === saved.id
            )
            if (index !== -1) {
                cookieConsentServices.value[index] = saved
            }
        }

        toast.success(
            i18n.t(
                isCreate
                    ? 'toast_success_cookie_consent_service_created'
                    : 'toast_success_cookie_consent_service_updated',
                {
                    service:
                        saved.name?.[i18n.locale.value as 'de' | 'en' | 'it'],
                }
            )
        )
        isModalOpen.value = false
    }

    async function handleLoadDefaultCookieConsentServices() {
        if (!platformName.value) return

        const response = await seedDefaultCookieConsentServices(
            platformName.value
        )
        cookieConsentServices.value = response.data.cookie_consent_services

        toast.success(i18n.t('toast_success_cookie_consent_services_seeded'))
    }

    const selectedCookieConsentServices = computed(() => {
        return cookieConsentServices.value.filter(
            (cookieConsentService) =>
                cookieConsentService.id &&
                selectedPlatformCookieConsentServicesIds.value.includes(
                    cookieConsentService.id
                )
        )
    })

    return {
        PlatformCookieConsentServicesTable,
        selectedPlatformCookieConsentServicesIds,
        handleDeletePlatformCookieConsentServices,
        handleEditOrCreateCookieConsentService,
        handleLoadDefaultCookieConsentServices,
    }
}
